import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../supabaseClient';
import { CheckCircle2, Loader2 } from 'lucide-react';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { profile, isAuthenticated, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const plan = searchParams.get('plan') || 'small';
  const isTrial = searchParams.get('trial') === 'true';
  
  // Check if user already had a trial (check both fields)
  const hasHadTrial = (profile as any)?.trial_started_at !== null || (profile as any)?.trial_ends_at !== null;
  const isUpgrading = isAuthenticated && hasHadTrial;
  
  // Force paid plan if upgrading (no second trial)
  const actuallyTrial = isTrial && !isUpgrading;

  // Redirect if not authenticated (but wait for auth to load first!)
  useEffect(() => {
    if (authLoading) return; // Wait for auth to finish loading
    
    if (!isAuthenticated) {
      navigate(`/register/broker?plan=${plan}&trial=${isTrial}`);
    }
  }, [authLoading, isAuthenticated, navigate, plan, isTrial]);

  // Plan details
    const planDetails = {
    starter: { name: 'Starter', price: 29, limit: 20 },
    professional: { name: 'Professional', price: 79, limit: 50 },
  enterprise: { name: 'Enterprise', price: 199, limit: 999 }
  };
 const selectedPlan = planDetails[plan as keyof typeof planDetails] || planDetails.starter;
const features = [
    'Carrier onboarding & compliance',
    'Document collection & storage',
    'Approve/reject workflow',
    'Insurance expiration tracking',
    'Rate confirmations & e-signatures',
    'Carrier compliance dashboard',
    selectedPlan.name === 'Enterprise' ? 'Dedicated account manager' :
    selectedPlan.name === 'Professional' ? 'Priority support' : 'Email support'
  ];
  const handleConfirm = async () => {
    setLoading(true);
    setError('');

    try {
      if (actuallyTrial) {
        // Start trial (no payment)
        const now = new Date();
        const trialEnd = new Date(now);
        trialEnd.setDate(trialEnd.getDate() + 7);
        
        const updateData = {
          trial_started_at: now.toISOString(),
          trial_ends_at: trialEnd.toISOString(),
          subscription_status: 'trial',
          carrier_limit: selectedPlan.limit,
        };

        const { error: updateError } = await supabase
          .from('profiles')
          .update(updateData)
          .eq('id', profile?.id);

        if (updateError) throw updateError;

        navigate('/broker/dashboard', {
          state: {
            message: `7-day trial activated for ${selectedPlan.name}!`
          }
        });
      } else {
        // Paid plan - Create Stripe checkout session
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;

        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
const response = await fetch(`${backendUrl}/stripe/create-checkout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ plan }),
        });

        if (!response.ok) {
          throw new Error('Failed to create checkout session');
        }

        const { url } = await response.json();
        
        // Redirect to Stripe checkout
        window.location.href = url;
      }

    } catch (err: any) {
      console.error('Error activating plan:', err);
      setError('Failed to activate plan. Please try again.');
      setLoading(false);
    }
  };

  if (authLoading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!isAuthenticated) {
    return <div className="min-h-screen flex items-center justify-center">Redirecting...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            {isUpgrading ? 'Upgrade Your Plan' : 
             actuallyTrial ? 'Start Your Free Trial' : 
             'Confirm Your Plan'}
          </h1>
          <p className="text-gray-600 mt-2">
            {isUpgrading 
              ? 'Select your new plan to continue with full access'
              : actuallyTrial 
              ? 'Get full access for 7 days. No credit card required.' 
              : 'Review your plan selection below'}
          </p>
        </div>

        {/* Plan Summary Card */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
          <div className="border-b border-gray-200 pb-6 mb-6">
            <h2 className="text-2xl font-bold text-gray-900">{selectedPlan.name}</h2>
            <div className="mt-2">
              <span className="text-4xl font-bold text-gray-900">${selectedPlan.price}</span>
              <span className="text-gray-600">/month</span>
            </div>
            <p className="text-sm text-gray-600 mt-2">
             Up to {selectedPlan.limit === 999 ? 'unlimited' : selectedPlan.limit} carriers
            </p>
          </div>

          {/* Features */}
          <div className="mb-6">
            <h3 className="font-semibold text-gray-900 mb-4">What's included:</h3>
            <ul className="space-y-3">
              {features.map((feature, index) => (
                <li key={index} className="flex items-center">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mr-3" />
                  <span className="text-gray-700">{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Plan Change Summary */}
          {profile && (
            <div className="bg-blue-50 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-blue-900 mb-2">Plan Change Summary</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-blue-600 font-medium">Current</p>
                  <p className="text-blue-800">
                    {(profile as any).subscription_status === 'trial' ? 'Free Trial' : 
                     (profile as any).subscription_status === 'active' ? 'Active Plan' : 'Expired'}
                  </p>
                  <p className="text-blue-800">{(profile as any).carrier_limit || 20} carriers</p>
                </div>
                <div>
                  <p className="text-green-600 font-medium">After Upgrade</p>
                  <p className="text-green-800">{selectedPlan.name}</p>
                 <p className="text-green-800">{selectedPlan.limit === 999 ? 'Unlimited' : selectedPlan.limit} carriers</p>
                </div>
              </div>
            </div>
          )}

          {/* Payment Notice */}
          {!actuallyTrial && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-800">
                <strong>Secure Payment:</strong> You'll be redirected to Stripe's secure checkout page to complete your payment.
              </p>
            </div>
          )}

          {/* Upgrade Notice */}
          {isUpgrading && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-green-800">
                <strong>Upgrading:</strong> You've already used your free trial. This upgrade will activate your paid plan immediately.
              </p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Confirm Button */}
          <button
            onClick={handleConfirm}
            disabled={loading}
            className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin h-5 w-5 mr-2" />
                Activating...
              </>
            ) : (
              actuallyTrial ? 'Start 7-Day Free Trial' : 'Proceed to Payment'
            )}
          </button>

          <p className="text-xs text-gray-500 text-center mt-4">
            {actuallyTrial 
              ? 'No credit card required. Cancel anytime during the trial.' 
              : 'You can change or cancel your plan anytime from settings.'}
          </p>
        </div>

        {/* Back to Pricing */}
        <div className="text-center">
          <a
            href="/pricing"
            className="text-blue-600 hover:underline text-sm"
          >
            ← Back to pricing
          </a>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
